import { foldOverlay, overlayShapeProblem } from './overlay.js';
export const MODIFIER_TO = ['attack', 'ac', 'save'];
export const MODIFIER_WHEN = ['always', 'chosen'];
/** The bounds: thirty rulings in force; the text a sentence; a bonus's label a few words (with " (ruling)", within a term's 40). */
export const RULING_LIMITS = { count: 30, text: 200, label: 30, value: 50 };
export const TOO_MANY_RULINGS = `A table holds at most ${RULING_LIMITS.count} rulings at once. Remove one first.`;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CONTROL_RE = /[\u0000-\u001f\u007f]/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !CONTROL_RE.test(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const hasNul = (v) => {
    if (typeof v === 'string')
        return v.includes('\u0000');
    if (Array.isArray(v))
        return v.some(hasNul);
    if (isObj(v))
        return Object.entries(v).some(([k, x]) => k.includes('\u0000') || hasNul(x));
    return false;
};
/** A bonus from anywhere: exact shape or null. */
export function parseRulingModifier(v) {
    if (!isObj(v))
        return null;
    const { label, to, value, when } = v;
    if (!isText(label, RULING_LIMITS.label) || !MODIFIER_TO.includes(to) || !MODIFIER_WHEN.includes(when))
        return null;
    if (!isInt(value, -RULING_LIMITS.value, RULING_LIMITS.value) || value === 0)
        return null;
    return { label: label.trim(), to: to, value, when: when };
}
/** Why an overlay cannot be a ruling's, or null: the shape alone (what it does to the rules is `foldRulings`'). */
export const rulingRuleProblem = (v) => (hasNul(v) ? 'A rule change holds plain text.' : overlayShapeProblem(v));
/** A ruling from anywhere (the wire, a stored state): exact shape or null. Unknown fields are dropped. */
export function parseRuling(v) {
    if (!isObj(v))
        return null;
    const { id, text, keep, at } = v;
    if (typeof id !== 'string' || !UUID_RE.test(id) || !isText(text, RULING_LIMITS.text) || typeof keep !== 'boolean')
        return null;
    if (typeof at !== 'string' || at.length > 40 || Number.isNaN(Date.parse(at)))
        return null;
    const head = { id, text: text.trim(), keep, at };
    if (v.rule !== undefined && v.modifier !== undefined)
        return null;
    if (v.modifier !== undefined) {
        const modifier = parseRulingModifier(v.modifier);
        return modifier ? { ...head, modifier } : null;
    }
    if (v.rule === undefined || rulingRuleProblem(v.rule))
        return null;
    const r = v.rule;
    return { ...head, rule: JSON.parse(JSON.stringify({ ...(r.combat !== undefined ? { combat: r.combat } : {}), ...(r.effects !== undefined ? { effects: r.effects } : {}) })) };
}
/** Stored rulings (a state from before issue 1136 has none): each read through the parser, at most the bound. */
export function normaliseRulings(v) {
    if (!Array.isArray(v))
        return [];
    const out = [];
    for (const x of v) {
        const r = parseRuling(x);
        if (r && !out.some((o) => o.id === r.id))
            out.push(r);
    }
    return out.slice(0, RULING_LIMITS.count);
}
export const RULING_KINDS = ['ruling.add', 'ruling.keep', 'ruling.remove', 'session.end'];
export const isRulingBody = (b) => RULING_KINDS.includes(b.kind);
/** A ruling event from anywhere: exact shape or null. */
export function parseRulingBody(v) {
    if (!isObj(v))
        return null;
    switch (v.kind) {
        case 'ruling.add': {
            const ruling = parseRuling(v.ruling);
            return ruling ? { kind: 'ruling.add', ruling } : null;
        }
        case 'ruling.keep': return typeof v.id === 'string' && UUID_RE.test(v.id) && typeof v.keep === 'boolean' ? { kind: 'ruling.keep', id: v.id, keep: v.keep } : null;
        case 'ruling.remove': return typeof v.id === 'string' && UUID_RE.test(v.id) ? { kind: 'ruling.remove', id: v.id } : null;
        case 'session.end': {
            if (v.lapsed === undefined)
                return { kind: 'session.end' };
            if (!Array.isArray(v.lapsed) || v.lapsed.length > RULING_LIMITS.count || !v.lapsed.every((x) => isText(x, RULING_LIMITS.text)))
                return null;
            return { kind: 'session.end', lapsed: v.lapsed.map(String) };
        }
        default: return null;
    }
}
/** Why an accepted-looking ruling event cannot be applied to these rulings, or null (the server's check). */
export function rulingRefusal(rulings, body) {
    if (body.kind === 'ruling.add') {
        if (rulings.length >= RULING_LIMITS.count)
            return TOO_MANY_RULINGS;
        if (rulings.some((r) => r.id === body.ruling.id))
            return 'That ruling is already in force.';
        return null;
    }
    if (body.kind === 'ruling.keep' || body.kind === 'ruling.remove')
        return rulings.some((r) => r.id === body.id) ? null : 'That ruling is no longer in force.';
    return null;
}
/** The rulings after an accepted event. */
export function reduceRulings(rulings, body) {
    switch (body.kind) {
        case 'ruling.add': return rulings.some((r) => r.id === body.ruling.id) ? rulings : [...rulings, body.ruling];
        case 'ruling.keep': return rulings.map((r) => (r.id === body.id ? { ...r, keep: body.keep } : r));
        case 'ruling.remove': return rulings.filter((r) => r.id !== body.id);
        case 'session.end': return rulings.filter((r) => r.keep);
    }
}
export const playerRulings = (rulings) => rulings.map((r) => ({ id: r.id, text: r.text, keep: r.keep, ...('modifier' in r ? { modifier: { ...r.modifier } } : {}) }));
/** A player's list from the wire: each exact or dropped. */
export function parsePlayerRulings(v) {
    if (!Array.isArray(v))
        return [];
    const out = [];
    for (const x of v.slice(0, RULING_LIMITS.count)) {
        if (!isObj(x) || typeof x.id !== 'string' || !UUID_RE.test(x.id) || !isText(x.text, RULING_LIMITS.text) || typeof x.keep !== 'boolean')
            continue;
        const modifier = x.modifier === undefined ? undefined : parseRulingModifier(x.modifier);
        if (modifier === null)
            continue;
        out.push({ id: x.id, text: x.text, keep: x.keep, ...(modifier ? { modifier } : {}) });
    }
    return out;
}
// ── Applying them ────────────────────────────────────────────────────────────
/** How a ruling names itself where it decided something: "ruling: Flanking gives +4 here", within a stored name's 120. */
export const rulingName = (text) => {
    const n = `ruling: ${text}`;
    return n.length <= 120 ? n : `${n.slice(0, 119)}…`;
};
/**
 * The registry's rules (`{ combat, effects: { stacking, units }, sources }`) with every `rule` ruling laid over them in
 * order, the top layer: each path a ruling set is named in `sources` by `rulingName` (shown to players: the list is
 * theirs). A ruling that would leave the rules unreadable is passed over whole and listed in `refused`. Null rules (a
 * system with none the site could give) stay null: a ruling changes rules, it does not make them.
 */
export function foldRulings(registry, rulings) {
    const applied = [];
    const refused = [];
    if (registry === null || registry === undefined)
        return { rules: registry ?? null, applied, refused: rulings.filter((r) => 'rule' in r).map((r) => r.id) };
    let current = registry;
    for (const r of rulings) {
        if (!('rule' in r))
            continue;
        const next = foldOverlay(current, r.rule, { layer: 0, name: rulingName(r.text), released: true, ruling: true });
        if (!next) {
            refused.push(r.id);
            continue;
        }
        current = next.rules;
        applied.push(r.id);
    }
    return { rules: current, applied, refused };
}
/** The term a bonus adds to the working: "+4 Flanking (ruling)". */
export const rulingTerm = (m) => ({ label: `${m.label} (ruling)`, value: m.value });
/**
 * The terms the rulings in force add to a roll `to` attack, AC or a save: every `always` one, and each `chosen` one
 * whose id the request carried. A chosen id that is not a chosen ruling of that kind in force is `unknown` (refused).
 */
export function rulingTerms(rulings, to, chosen = []) {
    const terms = [];
    for (const r of rulings) {
        if (!('modifier' in r) || r.modifier.to !== to)
            continue;
        if (r.modifier.when === 'always' || chosen.includes(r.id))
            terms.push(rulingTerm(r.modifier));
    }
    const unknown = chosen.filter((id) => !rulings.some((r) => r.id === id && 'modifier' in r && r.modifier.when === 'chosen' && r.modifier.to === to));
    return { terms, unknown };
}
//# sourceMappingURL=rulings.js.map